import React, { useEffect, useRef, useState } from 'react';
import { FaMapMarkerAlt, FaCrosshairs, FaExternalLinkAlt } from 'react-icons/fa';
import { normalizeLocationDetail, getServiceMapUrl } from '../../utils/location';
import Highlight from '../../utils/Highlight';
import './LocationInput.scss';

// LocationInput — drop-in replacement for the plain location <input>.
// - Typing still works exactly like before (no disturbance).
// - Shows free address suggestions (Photon primary + Nominatim fallback, no API key).
// - "Use current location" fills GPS coords AND reverse-geocodes the place name.
// - Shows "Open in Map" link when a map URL is available.
//
// Props:
//   value: string (service.location)
//   detail: object|null (service.locationDetail)
//   onChange(locationString, locationDetail|null)
//   placeholder, disabled
const LocationInput = ({ value = '', detail = null, onChange, placeholder = 'Enter event location', disabled = false }) => {
  const [text, setText] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searchedEmpty, setSearchedEmpty] = useState(false);
  const [locating, setLocating] = useState(false);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);
  const requestIdRef = useRef(0);

  // Keep internal text in sync when parent changes (edit mode, same-as-above, etc.)
  useEffect(() => {
    setText(value || '');
  }, [value]);

  useEffect(() => {
    const handleOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  const buildPhotonAddress = (props = {}) => {
    const parts = [
      props.name,
      props.street || props.housenumber,
      props.district || props.locality,
      props.city,
      props.state,
      props.country,
    ]
      .map((p) => String(p || '').trim())
      .filter(Boolean);
    // de-dupe repeated names (e.g. name == city)
    return [...new Set(parts)].join(', ');
  };

  const searchPhoton = async (q) => {
    // location_bias_scale steers ranking toward India (app is India-first)
    // without excluding world results; limit raised for better coverage.
    const url = `https://photon.komoot.io/api/?q=${encodeURIComponent(q)}&limit=8&lang=en&lat=21.14&lon=78.88&location_bias_scale=0.6`;
    // Public keyless map APIs need NO cookies/auth. credentials:'omit' is required:
    // the app's fetch interceptor adds credentials:'include' to calls without it,
    // and browsers reject wildcard CORS (*) responses when credentials are included.
    const res = await fetch(url, { headers: { Accept: 'application/json' }, credentials: 'omit' });
    if (!res.ok) throw new Error(`photon ${res.status}`);
    const data = await res.json();
    const features = Array.isArray(data?.features) ? data.features : [];
    return features
      .map((f) => {
        const props = f?.properties || {};
        const coords = f?.geometry?.coordinates || [];
        const lng = coords.length >= 2 ? Number(coords[0]) : null;
        const lat = coords.length >= 2 ? Number(coords[1]) : null;
        const address = buildPhotonAddress(props);
        if (!address) return null;
        return {
          address,
          lat: Number.isFinite(lat) ? lat : null,
          lng: Number.isFinite(lng) ? lng : null,
          placeId: String(props.osm_id || `${lat},${lng}` || address).slice(0, 80),
          source: 'photon',
        };
      })
      .filter(Boolean);
  };

  const searchNominatim = async (q, indiaOnly = false) => {
    const countryParam = indiaOnly ? '&countrycodes=in' : '';
    const url = `https://nominatim.openstreetmap.org/search?format=jsonv2&addressdetails=1&dedupe=1&limit=8${countryParam}&q=${encodeURIComponent(q)}`;
    // Public keyless map APIs need NO cookies/auth. credentials:'omit' is required:
    // the app's fetch interceptor adds credentials:'include' to calls without it,
    // and browsers reject wildcard CORS (*) responses when credentials are included.
    const res = await fetch(url, { headers: { Accept: 'application/json' }, credentials: 'omit' });
    if (!res.ok) throw new Error(`nominatim ${res.status}`);
    const data = await res.json();
    return (Array.isArray(data) ? data : [])
      .map((d) => ({
        address: d.display_name || '',
        lat: d.lat !== undefined ? Number(d.lat) : null,
        lng: d.lon !== undefined ? Number(d.lon) : null,
        placeId: String(d.place_id || d.osm_id || ''),
        source: 'osm',
      }))
      .filter((s) => s.address);
  };

  const dedupeKey = (s) => String(s?.address || '').toLowerCase().replace(/[^a-z0-9]/g, '');

  const mergeSuggestions = (...lists) => {
    const seen = new Set();
    const out = [];
    for (const list of lists) {
      for (const s of Array.isArray(list) ? list : []) {
        const key = dedupeKey(s) || `${s?.lat},${s?.lng}`;
        if (!key || seen.has(key)) continue;
        seen.add(key);
        out.push(s);
        if (out.length >= 8) return out;
      }
    }
    return out;
  };

  const fetchSuggestions = async (query) => {
    const q = String(query || '').trim();
    const myRequest = ++requestIdRef.current;
    if (q.length < 3) {
      setSuggestions([]);
      setSearchedEmpty(false);
      setSearching(false);
      return;
    }
    setSearching(true);
    setSearchedEmpty(false);
    let mapped = [];
    try {
      // Photon first: fast + browser-friendly CORS, now India-biased.
      try {
        mapped = await searchPhoton(q);
      } catch (e) {
        mapped = [];
      }
      // Top up thin results with India-biased Nominatim, then global
      // Nominatim — covers specific Indian venues Photon sometimes misses.
      if (mapped.length < 3) {
        try {
          const india = await searchNominatim(q, true);
          mapped = mergeSuggestions(mapped, india);
        } catch (e) {
          // keep photon results — typing still works
        }
      }
      if (mapped.length === 0) {
        try {
          const global = await searchNominatim(q, false);
          mapped = mergeSuggestions(mapped, global);
        } catch (e) {
          // keep existing results (possibly empty) — typing still works
        }
      }
    } finally {
      // Ignore stale responses when user keeps typing
      if (requestIdRef.current === myRequest) {
        setSuggestions(mapped);
        setSearchedEmpty(mapped.length === 0);
        setOpen(true);
        setSearching(false);
      }
    }
  };

  // Reverse-geocode GPS coords -> human place name.
  // Tries BigDataCloud (built for browsers) + Nominatim (full address), picks best.
  const reverseGeocodeName = async (latitude, longitude) => {
    const lat = Number(latitude);
    const lng = Number(longitude);
    const jobs = [];

    jobs.push(
      (async () => {
        const url = `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lng}&localityLanguage=en`;
        // Public keyless map APIs need NO cookies/auth. credentials:'omit' is required:
    // the app's fetch interceptor adds credentials:'include' to calls without it,
    // and browsers reject wildcard CORS (*) responses when credentials are included.
    const res = await fetch(url, { headers: { Accept: 'application/json' }, credentials: 'omit' });
        if (!res.ok) throw new Error('bigdata failed');
        const d = await res.json();
        const parts = [d.locality || d.city, d.city && d.locality !== d.city ? d.city : '', d.principalSubdivision, d.countryName]
          .map((p) => String(p || '').trim())
          .filter(Boolean);
        const unique = [...new Set(parts)];
        return unique.join(', ');
      })().catch(() => '')
    );

    jobs.push(
      (async () => {
        const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&zoom=16`;
        // Public keyless map APIs need NO cookies/auth. credentials:'omit' is required:
    // the app's fetch interceptor adds credentials:'include' to calls without it,
    // and browsers reject wildcard CORS (*) responses when credentials are included.
    const res = await fetch(url, { headers: { Accept: 'application/json' }, credentials: 'omit' });
        if (!res.ok) throw new Error('reverse failed');
        const d = await res.json();
        return String(d.display_name || '').trim();
      })().catch(() => '')
    );

    const [a, b] = await Promise.all(jobs);
    // Prefer the fuller address (usually Nominatim display_name)
    const best = [b, a].find((s) => s && s.trim().length > 0) || '';
    return best.trim();
  };

  const handleTextChange = (e) => {
    const val = e.target.value;
    setText(val);
    // Manual typing clears the old pin (avoids wrong map link), but keeps text.
    // Parent still receives plain string immediately so save/draft logic is untouched.
    if (onChange) onChange(val, null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(val), 400);
  };

  const pickSuggestion = (s) => {
    const normalized = normalizeLocationDetail(s);
    const label = normalized ? normalized.address : s.address;
    setText(label);
    setSuggestions([]);
    setSearchedEmpty(false);
    setOpen(false);
    if (onChange) onChange(label, normalized);
  };

  const useCurrentLocation = () => {
    if (!navigator || !navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords || {};
        try {
          // 1) Fill coords immediately so map link works even if name lookup is slow
          const quick = normalizeLocationDetail({
            address: text.trim() || `${latitude},${longitude}`,
            lat: latitude,
            lng: longitude,
            source: 'gps',
          });
          setText(quick.address);
          if (onChange) onChange(quick.address, quick);
          // 2) Look up the real place name, then replace coords text with the name
          const name = await reverseGeocodeName(latitude, longitude);
          if (name) {
            const named = normalizeLocationDetail({ address: name, lat: latitude, lng: longitude, source: 'gps-reverse' });
            setText(named.address);
            if (onChange) onChange(named.address, named);
          }
        } finally {
          setLocating(false);
        }
        setOpen(false);
      },
      () => setLocating(false),
      { enableHighAccuracy: true, timeout: 15000 }
    );
  };

  const mapUrl = getServiceMapUrl({ location: text, locationDetail: detail });

  return (
    <div className="location-input" ref={containerRef}>
      <div className="location-input__row">
        <input
          type="text"
          placeholder={placeholder}
          value={text}
          disabled={disabled || locating}
          onChange={handleTextChange}
          onFocus={() => { if (suggestions.length > 0) setOpen(true); }}
          autoComplete="off"
        />
        {typeof navigator !== 'undefined' && navigator.geolocation && !disabled && (
          <button
            type="button"
            className={`location-input__gps${locating ? ' is-locating' : ''}`}
            onClick={useCurrentLocation}
            disabled={locating}
            title="Use my current location (fills place name + map)"
          >
            <FaCrosshairs /> {locating ? '...' : ''}
          </button>
        )}
      </div>

      {open && suggestions.length > 0 && (
        <ul className="location-input__dropdown">
          {suggestions.map((s, i) => (
            <li key={`${s.placeId}-${i}`}>
              <button type="button" onClick={() => pickSuggestion(s)} title={s.address}>
                <FaMapMarkerAlt className="location-input__pin" />
                <span><Highlight text={s.address} query={text.trim()} /></span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {searching && <div className="location-input__hint">Searching places…</div>}
      {!searching && open && searchedEmpty && text.trim().length >= 3 && (
        <div className="location-input__hint">No matches — your typed text will be saved with a map link.</div>
      )}
      {locating && <div className="location-input__hint">Getting your location… fetching place name…</div>}

      {mapUrl && text.trim() && (
        <a
          className="location-input__maplink"
          href={mapUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
        >
          <FaExternalLinkAlt /> Open in Google Maps
        </a>
      )}
    </div>
  );
};

export default LocationInput;
